<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalSubText from "@/components/global-themed/ui/modals/ModalSubText.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";
import KitModalHeader from "@/components/global-themed/ui/modals/KitModalHeader.vue";
import ModalTextAreaWrapper from "@/components/global-themed/ui/modals/ModalTextAreaWrapper.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import AlertBox from "@/components/global-themed/AlertBox.vue";
import {ref} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast} from "@/composables/useToasts";

const helpInput = ref('');

const props = defineProps({
  screen: {
    type: String,
    required: true,
  },
});

const errors = ref({});

const {mutate, isPending, isSuccess} = useMutation({
  mutationFn: (data) => axios.post('/ajax/v2/kit/help', data),
  onError: (error) => {
    if (error.response?.status === 422) {
      errorToast('Check for errors and try again');
      errors.value = error.response?.data?.errors;
      return
    }

    errorToast('Something went wrong, please try again');
    console.error(error);
  }
});

const handleSubmit = () => {
  mutate({
    text: helpInput.value,
    screen: props.screen,
  })
}

const emit = defineEmits(['close', 'confirm']);
</script>

<template>
  <Teleport
    to=".tailwind"
  >
    <BaseModal
      modal-class="tw-w-[800px]"
      @close="emit('close')"
    >
      <ModalContentWrapper>
        <KitModalHeader>
          <KitIcon
            class="tw-w-[88px]"
            state="error"
          />

          <ModalHeader>
            <ModalHeading>How can we help?</ModalHeading>

            <ModalSubText>
              <p>Tell me which parts of your SEO strategy you’d like to change — the more specific you can be, the better.</p>
              <p>Once you’ve shared your feedback below, click Continue and your account manager will be in touch by the next working day, if not sooner!</p>
            </ModalSubText>
          </ModalHeader>
        </KitModalHeader>

        <AlertBox
          v-if="isSuccess"
          type="success"
        >
          <p class="tw-text-inherit">
            Thanks! One of your account managers will be in touch within the next working day or sooner
          </p>
        </AlertBox>

        <div
          v-else
          class="tw-flex tw-flex-col tw-gap-6"
        >
          <span class="tw-flex tw-gap-3 tw-text-neutral-900 tw-font-bold tw-text-2xl tw-leading-none">
            <FixPanelIcon icon-path="icons/users-talking" />
            What do you need help with?
          </span>

          <ModalTextAreaWrapper :error="!!errors?.text">
            <div>
              <textarea
                v-model="helpInput"
                placeholder=""
                rows="8"
                class="tw-w-full tw-text-neutral-900 tw-text-base tw-resize-none"
              />
              <p
                v-if="errors?.text"
                class="tw-text-danger"
              >
                {{ errors.text[0] }}
              </p>
            </div>
          </ModalTextAreaWrapper>
        </div>

        <div class="tw-flex tw-items-center tw-gap-4 tw-self-end">
          <DefaultButton
            variant="primary-inverted"
            @click="emit('close')"
          >
            {{ isSuccess ? 'Close' : 'Cancel' }}
          </DefaultButton>
          <DefaultButton
            v-if="!isSuccess"
            :loading="isPending"
            :disabled="!helpInput || isPending || isSuccess"
            @click="handleSubmit"
          >
            Continue
          </DefaultButton>
        </div>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
